"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useTransition, type ComponentProps, type ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { PortableText, stegaClean } from "next-sanity";
import { Modal } from "@repo/ui/mantine";
import { Typography } from "@repo/ui/aops-components/typography";
import { getPortableTextComponents, type PortableTextLinkMarkValue } from "@repo/ui/aops-components/portable-text-components";
import { DiscoveryButton } from "@/app/components/DiscoveryButton";
import { acceptPrivacyPolicyAction, persistPrivacyPolicyNoticeAction } from "@/app/actions/acceptPrivacyPolicy";
import { isPrivacyPolicyPath } from "@/lib/privacy/isPrivacyPolicyPath";
import { isAcceptPrivacyPolicyButton, type PrivacyPolicyAnnouncement } from "@/lib/privacy/privacyPolicyAnnouncement";
import type { PrivacyPolicyNoticeValue } from "@/lib/privacy/privacyPolicyNoticeCookie";
import portableTextStyles from "../PortableText/PortableTextComponents.module.css";
import styles from "./PrivacyPolicyChange2026Modal.module.css";
export type PrivacyPolicyChange2026ModalProps = {
  show: boolean;
  needsPersist: boolean;
  persistValue?: PrivacyPolicyNoticeValue;
  announcement: PrivacyPolicyAnnouncement | null;
};
const announcementPortableTextComponents = (() => {
  const components = getPortableTextComponents({
    classNames: {
      paragraph: portableTextStyles.paragraph,
      heading: portableTextStyles.heading,
      list: portableTextStyles.list,
      listItem: portableTextStyles.listItem,
      listNumber: portableTextStyles.listNumber,
      listItemNumber: portableTextStyles.listItemNumber
    }
  });
  return {
    ...components,
    marks: {
      ...components.marks,
      link: AnnouncementLinkMark
    }
  };
})() as ComponentProps<typeof PortableText>["components"];
function AnnouncementLinkMark(t0) {
  const $ = _c(19);
  const {
    children,
    value
  } = t0;
  let href;
  let newTabProps;
  let t1;
  let t2;
  if ($[0] !== children || $[1] !== value?.href || $[2] !== value?.openInNewTab) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      href = stegaClean(value?.href);
      if (!href) {
        let t3;
        if ($[7] !== children) {
          t3 = <>{children}</>;
          $[7] = children;
          $[8] = t3;
        } else {
          t3 = $[8];
        }
        t2 = t3;
        break bb0;
      }
      const openInNewTab = value?.openInNewTab === true;
      let t3;
      if ($[9] !== openInNewTab) {
        t3 = openInNewTab ? {
          target: "_blank",
          rel: "noopener noreferrer"
        } : {};
        $[9] = openInNewTab;
        $[10] = t3;
      } else {
        t3 = $[10];
      }
      newTabProps = t3;
      t1 = href.startsWith("/");
    }
    $[0] = children;
    $[1] = value?.href;
    $[2] = value?.openInNewTab;
    $[3] = href;
    $[4] = newTabProps;
    $[5] = t1;
    $[6] = t2;
  } else {
    href = $[3];
    newTabProps = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  if (t1) {
    let t3;
    if ($[11] !== children || $[12] !== href || $[13] !== newTabProps) {
      t3 = <Link href={href} {...newTabProps}>{children}</Link>;
      $[11] = children;
      $[12] = href;
      $[13] = newTabProps;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    return t3;
  }
  let t3;
  if ($[15] !== children || $[16] !== href || $[17] !== newTabProps) {
    t3 = <a href={href} {...newTabProps}>{children}</a>;
    $[15] = children;
    $[16] = href;
    $[17] = newTabProps;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  return t3;
}
export function PrivacyPolicyChange2026Modal(t0) {
  const $ = _c(21);
  const {
    show,
    needsPersist,
    persistValue,
    announcement
  } = t0;
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(show);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isPending, startTransition] = useTransition();
  let t1;
  let t2;
  if ($[0] !== needsPersist || $[1] !== persistValue) {
    t1 = () => {
      if (needsPersist && persistValue) {
        persistPrivacyPolicyNoticeAction(persistValue);
      }
    };
    t2 = [needsPersist, persistValue];
    $[0] = needsPersist;
    $[1] = persistValue;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (!isOpen || isPrivacyPolicyPath(pathname) || !announcement) {
    return null;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function handleAgree() {
      setErrorMessage(null);
      startTransition(async () => {
        const result = await acceptPrivacyPolicyAction();
        if (!result.success) {
          setErrorMessage(result.errorMessage);
          return;
        }
        setIsOpen(false);
      });
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleAgree = t3;
  let t4;
  if ($[5] !== announcement.title) {
    t4 = <Typography variant="h4" as="span" className={styles.title}>{announcement.title}</Typography>;
    $[5] = announcement.title;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      content: styles.content,
      body: styles.body
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== announcement.body) {
    t6 = <div className={portableTextStyles.portableText}><PortableText value={announcement.body} components={announcementPortableTextComponents} /></div>;
    $[8] = announcement.body;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== errorMessage) {
    t7 = errorMessage && <Typography variant="bodySmall" className={styles.errorMessage} role="alert">{errorMessage}</Typography>;
    $[10] = errorMessage;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== announcement.buttons || $[13] !== isPending) {
    t8 = announcement.buttons.length > 0 && <div className={styles.actions}>{announcement.buttons.map(button => {
        const accept = isAcceptPrivacyPolicyButton(button.link);
        return <DiscoveryButton key={button._key} button={accept && isPending ? {
          ...button,
          disabled: true
        } : button} onClick={accept ? handleAgree : undefined} />;
      })}</div>;
    $[12] = announcement.buttons;
    $[13] = isPending;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isOpen || $[16] !== t4 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = <Modal opened={isOpen} onClose={_temp} centered={true} withCloseButton={false} closeOnClickOutside={false} closeOnEscape={false} title={t4} classNames={t5}>{t6}{t7}{t8}</Modal>;
    $[15] = isOpen;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
function _temp() {}